import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../hooks/useApi';
import {
  UsersIcon, TrendUpIcon, BarChartIcon, ShieldIcon, StarIcon,
  CurrencyIcon, AlertIcon, CheckIcon, RefreshIcon, ZapIcon,
  PackageIcon, ClockIcon, ReceiptIcon, ArrowRightIcon, BadgeCheckIcon,
} from '../../components/Icons';

// ── Formatadores ──────────────────────────────────────────────────────────────

const fmtKz = (v) => {
  if (!v && v !== 0) return '—';
  if (v === 0) return '0 Kz';
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M Kz';
  if (v >= 1_000) return (v / 1_000).toFixed(0) + 'K Kz';
  return v.toFixed(0) + ' Kz';
};

const fmtNum = (v) =>
  new Intl.NumberFormat('pt-PT').format(v || 0);

const fmtPct = (v) => (v == null ? '—' : `${v}%`);

const fmtHrs = (v) => {
  if (!v) return '—';
  if (v < 1) return `${Math.round(v * 60)} min`;
  if (v < 24) return `${v.toFixed(1)} h`;
  return `${(v / 24).toFixed(1)} dias`;
};

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, icon, cor = '#2563EB' }) {
  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: '12px',
      padding: '16px',
      position: 'relative',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s, transform 0.2s',
    }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = 'var(--shadow)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* top colour strip */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
        background: cor, borderRadius: '12px 12px 0 0',
      }} />
      {/* icon badge top-right */}
      <div style={{
        position: 'absolute', top: '14px', right: '14px',
        width: '30px', height: '30px', borderRadius: '8px',
        background: cor + '18',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </div>
      <div style={{
        fontSize: '10px', color: 'var(--text-muted)', fontWeight: '600',
        textTransform: 'uppercase', letterSpacing: '0.5px',
        marginBottom: '8px', paddingRight: '36px',
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '24px', fontWeight: '800', fontFamily: 'Syne, sans-serif',
        color: 'var(--text-primary)', letterSpacing: '-0.5px', lineHeight: 1,
      }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// ── Category Section ──────────────────────────────────────────────────────────

function Seccao({ titulo, subtitulo, cor, icone, children }) {
  return (
    <div style={{
      background: 'var(--card)',
      border: '1px solid var(--border)',
      borderRadius: '16px',
      overflow: 'hidden',
      marginBottom: '20px',
      boxShadow: 'var(--shadow-sm)',
    }}>
      {/* gradient header band */}
      <div style={{
        padding: '16px 20px',
        background: `linear-gradient(135deg, ${cor}12, ${cor}06)`,
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: '14px',
      }}>
        {/* icon with glow */}
        <div style={{
          width: '40px', height: '40px', borderRadius: '10px',
          background: `linear-gradient(135deg, ${cor}, ${cor}CC)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 4px 12px ${cor}40`, flexShrink: 0,
        }}>
          {icone}
        </div>
        <div>
          <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)' }}>
            {titulo}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>
            {subtitulo}
          </div>
        </div>
        {/* decorative SVG top-right */}
        <div style={{ marginLeft: 'auto', opacity: 0.06, flexShrink: 0 }}>
          <svg width="60" height="40" viewBox="0 0 60 40">
            <circle cx="10" cy="20" r="8"  fill={cor} />
            <circle cx="30" cy="20" r="6"  fill={cor} />
            <circle cx="48" cy="20" r="10" fill={cor} />
          </svg>
        </div>
      </div>
      {/* KPI grid */}
      <div style={{
        padding: '16px 20px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
        gap: '12px',
      }}>
        {children}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function DashboardAdmin() {
  const navigate = useNavigate();
  const [metricas,   setMetricas]   = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro,       setErro]       = useState(null);
  const [ultimaAtt,  setUltimaAtt]  = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const d = await apiFetch('/admin/dashboard');
      setMetricas(d.metricas);
      setUltimaAtt(new Date());
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const A  = metricas?.aquisicao    || {};
  const AT = metricas?.ativacao     || {};
  const U  = metricas?.utilizacao   || {};
  const F  = metricas?.financeiras  || {};
  const R  = metricas?.retencao     || {};

  const totalFontes =
    (A.fontes?.google || 0) + (A.fontes?.email || 0) + (A.fontes?.telefone || 0);

  return (
    <>
      {/* ── Topbar ───────────────────────────────────────────────────────────── */}
      <div className="topbar">
        <div>
          <div className="topbar-title">Dashboard de Métricas</div>
          <div className="topbar-sub">
            {ultimaAtt
              ? `Actualizado às ${ultimaAtt.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`
              : 'Indicadores chave de performance da plataforma'}
          </div>
        </div>
        <div className="topbar-actions">
          <button
            className="btn btn-ghost"
            onClick={carregar}
            disabled={carregando}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <RefreshIcon size={14} style={carregando ? { animation: 'spin 1s linear infinite' } : {}} />
            {carregando ? 'A carregar…' : 'Actualizar'}
          </button>
        </div>
      </div>

      <div className="page-content">

        {/* ── Error alert ──────────────────────────────────────────────────── */}
        {erro && (
          <div style={{
            padding: '14px 18px', borderRadius: '12px', marginBottom: '20px',
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
            display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--danger)',
            boxShadow: '0 2px 8px rgba(239,68,68,0.1)',
          }}>
            <AlertIcon size={16} />
            <span style={{ fontWeight: '500', flex: 1 }}>
              Erro ao carregar métricas: {erro}
            </span>
            <button className="btn btn-outline btn-sm" onClick={carregar} style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}>
              Tentar novamente
            </button>
          </div>
        )}

        {/* ── Loading state ─────────────────────────────────────────────────── */}
        {carregando && !metricas ? (
          <div style={{ padding: '80px', textAlign: 'center' }}>
            <svg width="60" height="60" viewBox="0 0 60 60" style={{ animation: 'pulse 1.5s infinite' }}>
              <rect x="8" y="8" width="44" height="44" rx="12" fill="#FFF7ED" />
              <text x="30" y="38" textAnchor="middle" fontSize="24" fontWeight="800" fontFamily="Syne,sans-serif" fill="#F97316">K</text>
            </svg>
            <style>{`@keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.6;transform:scale(0.95)}} @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
            <div style={{ marginTop: '16px', fontSize: '14px', fontWeight: '600', color: 'var(--text-secondary)' }}>
              A calcular métricas…
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--text-muted)' }}>
              Aguarda enquanto processamos os dados
            </div>
          </div>
        ) : (
          <>
            <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

            {/* ── Hero strip — 4 gradient cards ─────────────────────────────── */}
            <div className="grid-4" style={{ marginBottom: '24px' }}>

              {/* Utilizadores */}
              <div style={{
                background: 'linear-gradient(135deg, #1A4A8A, #2563EB)',
                borderRadius: '14px', padding: '20px', color: '#fff',
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', right: '-20px', top: '-20px', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                <div style={{ fontSize: '11px', fontWeight: '600', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                  Utilizadores
                </div>
                <div style={{ fontSize: '32px', fontWeight: '800', fontFamily: 'Syne, sans-serif', letterSpacing: '-1px' }}>
                  {fmtNum(A.utilizadores_registados)}
                </div>
                <div style={{ fontSize: '11px', opacity: 0.65, marginTop: '4px' }}>registados na plataforma</div>
                <div style={{ position: 'absolute', right: '16px', bottom: '16px', opacity: 0.2 }}>
                  <UsersIcon size={28} color="#fff" />
                </div>
              </div>

              {/* GMV */}
              <div style={{
                background: 'linear-gradient(135deg, #059669, #10B981)',
                borderRadius: '14px', padding: '20px', color: '#fff',
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', right: '-20px', top: '-20px', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                <div style={{ fontSize: '11px', fontWeight: '600', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                  GMV
                </div>
                <div style={{ fontSize: '28px', fontWeight: '800', fontFamily: 'Syne, sans-serif', letterSpacing: '-1px' }}>
                  {fmtKz(U.gmv)}
                </div>
                <div style={{ fontSize: '11px', opacity: 0.65, marginTop: '4px' }}>valor bruto de mercadoria</div>
                <div style={{ position: 'absolute', right: '16px', bottom: '16px', opacity: 0.2 }}>
                  <CurrencyIcon size={28} color="#fff" />
                </div>
              </div>

              {/* Transações */}
              <div style={{
                background: 'linear-gradient(135deg, #F97316, #FB923C)',
                borderRadius: '14px', padding: '20px', color: '#fff',
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', right: '-20px', top: '-20px', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                <div style={{ fontSize: '11px', fontWeight: '600', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                  Transações
                </div>
                <div style={{ fontSize: '32px', fontWeight: '800', fontFamily: 'Syne, sans-serif', letterSpacing: '-1px' }}>
                  {fmtNum(U.transacoes_processadas)}
                </div>
                <div style={{ fontSize: '11px', opacity: 0.65, marginTop: '4px' }}>reservas processadas</div>
                <div style={{ position: 'absolute', right: '16px', bottom: '16px', opacity: 0.2 }}>
                  <ReceiptIcon size={28} color="#fff" />
                </div>
              </div>

              {/* Rating médio */}
              <div style={{
                background: 'linear-gradient(135deg, #7C3AED, #A855F7)',
                borderRadius: '14px', padding: '20px', color: '#fff',
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', right: '-20px', top: '-20px', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                <div style={{ fontSize: '11px', fontWeight: '600', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                  Rating médio
                </div>
                <div style={{ fontSize: '32px', fontWeight: '800', fontFamily: 'Syne, sans-serif', letterSpacing: '-1px' }}>
                  {R.avaliacao_media ? `${R.avaliacao_media}★` : '—'}
                </div>
                <div style={{ fontSize: '11px', opacity: 0.65, marginTop: '4px' }}>satisfação dos clientes</div>
                <div style={{ position: 'absolute', right: '16px', bottom: '16px', opacity: 0.2 }}>
                  <StarIcon size={28} color="#fff" />
                </div>
              </div>

            </div>

            {/* ── 1. AQUISIÇÃO ──────────────────────────────────────────────── */}
            <Seccao
              titulo="Aquisição"
              subtitulo="Como os utilizadores chegam à plataforma"
              cor="#2563EB"
              icone={<UsersIcon size={18} color="#fff" />}
            >
              <KpiCard
                label="Utilizadores registados"
                value={fmtNum(A.utilizadores_registados)}
                sub="Total activos na plataforma"
                icon={<UsersIcon size={14} color="#2563EB" />}
                cor="#2563EB"
              />
              <KpiCard
                label="Taxa de verificação"
                value={fmtPct(A.taxa_verificacao)}
                sub="Utilizadores KYC aprovados"
                icon={<CheckIcon size={14} color="#16A34A" />}
                cor="#16A34A"
              />

              {/* Acquisition sources bar chart */}
              <div style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: '12px', padding: '16px', gridColumn: 'span 2',
              }}>
                <div style={{
                  fontSize: '10px', color: 'var(--text-muted)', fontWeight: '700',
                  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px',
                }}>
                  📊 Fontes de Aquisição
                </div>
                {[
                  { label: 'Email / Password', value: A.fontes?.email    || 0, cor: '#2563EB', icon: '✉' },
                  { label: 'Google OAuth',     value: A.fontes?.google   || 0, cor: '#DB4437', icon: 'G' },
                  { label: 'Telefone / OTP',   value: A.fontes?.telefone || 0, cor: '#0F9D58', icon: '📱' },
                ].map(fonte => {
                  const pct = totalFontes > 0 ? Math.round(fonte.value / totalFontes * 100) : 0;
                  return (
                    <div key={fonte.label} style={{ marginBottom: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '5px' }}>
                        <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>
                          {fonte.icon} {fonte.label}
                        </span>
                        <span style={{ fontWeight: '700', color: 'var(--text-primary)' }}>
                          {fmtNum(fonte.value)}{' '}
                          <span style={{ color: 'var(--text-muted)', fontWeight: '500' }}>({pct}%)</span>
                        </span>
                      </div>
                      <div style={{ height: '8px', background: 'var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${pct}%`, height: '100%',
                          background: `linear-gradient(90deg, ${fonte.cor}, ${fonte.cor}99)`,
                          borderRadius: '4px', transition: 'width 0.8s ease',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Seccao>

            {/* ── 2. ATIVAÇÃO ───────────────────────────────────────────────── */}
            <Seccao
              titulo="Ativação"
              subtitulo="Progresso dos utilizadores até à primeira acção de valor"
              cor="#7C3AED"
              icone={<ZapIcon size={18} color="#fff" />}
            >
              <KpiCard
                label="Utilizadores verificados"
                value={fmtNum(AT.utilizadores_verificados)}
                sub="KYC completo"
                icon={<CheckIcon size={14} color="#7C3AED" />}
                cor="#7C3AED"
              />
              <KpiCard
                label="Tempo até 1.ª reserva"
                value={fmtHrs(AT.tempo_ate_1a_reserva_horas)}
                sub="Média após registo"
                icon={<ClockIcon size={14} color="#D97706" />}
                cor="#D97706"
              />
              <KpiCard
                label="Fornecedores activos"
                value={fmtNum(AT.fornecedores_ativos)}
                sub="Com conta activa"
                icon={<PackageIcon size={14} color="#059669" />}
                cor="#059669"
              />
              <KpiCard
                label="Taxa anúncios completos"
                value={fmtPct(AT.taxa_anuncios_completos)}
                sub={`${fmtNum(AT.anuncios_ativos)} anúncios activos`}
                icon={<BarChartIcon size={14} color="#7C3AED" />}
                cor="#7C3AED"
              />
            </Seccao>

            {/* ── 3. UTILIZAÇÃO ─────────────────────────────────────────────── */}
            <Seccao
              titulo="Utilização"
              subtitulo="Volume e qualidade das transações na plataforma"
              cor="#F97316"
              icone={<ReceiptIcon size={18} color="#fff" />}
            >
              <KpiCard
                label="Transações processadas"
                value={fmtNum(U.transacoes_processadas)}
                sub="Total de reservas criadas"
                icon={<ReceiptIcon size={14} color="#F97316" />}
                cor="#F97316"
              />
              <KpiCard
                label="GMV"
                value={fmtKz(U.gmv)}
                sub="Valor bruto de mercadoria"
                icon={<CurrencyIcon size={14} color="#16A34A" />}
                cor="#16A34A"
              />
              <KpiCard
                label="Ticket médio"
                value={fmtKz(U.ticket_medio)}
                sub="Valor médio por reserva"
                icon={<TrendUpIcon size={14} color="#2563EB" />}
                cor="#2563EB"
              />
              <KpiCard
                label="Taxa de cancelamento"
                value={fmtPct(U.taxa_cancelamento)}
                sub={`${fmtNum(U.reservas_concluidas)} concluídas`}
                icon={<AlertIcon size={14} color={U.taxa_cancelamento > 20 ? '#EF4444' : '#D97706'} />}
                cor={U.taxa_cancelamento > 20 ? '#EF4444' : '#D97706'}
              />
            </Seccao>

            {/* ── 4. FINANCEIRAS ────────────────────────────────────────────── */}
            <Seccao
              titulo="Financeiras"
              subtitulo="Receita gerada e saúde financeira da plataforma"
              cor="#059669"
              icone={<CurrencyIcon size={18} color="#fff" />}
            >
              <KpiCard
                label="Receita bruta"
                value={fmtKz(F.receita_bruta)}
                sub="Comissão KiAluga (10% GMV)"
                icon={<CurrencyIcon size={14} color="#059669" />}
                cor="#059669"
              />
              <KpiCard
                label="Take rate efectivo"
                value={fmtPct(F.take_rate_efectivo)}
                sub="Comissão média sobre GMV"
                icon={<TrendUpIcon size={14} color="#2563EB" />}
                cor="#2563EB"
              />
              <KpiCard
                label="Margem por transação"
                value={fmtKz(F.margem_por_transacao)}
                sub="Receita média por reserva"
                icon={<BarChartIcon size={14} color="#7C3AED" />}
                cor="#7C3AED"
              />
              <KpiCard
                label="Burn rate"
                value="N/D"
                sub="Requer dados de custos"
                icon={<AlertIcon size={14} color="#94a3b8" />}
                cor="#94a3b8"
              />

              {/* Receita vs GMV visual */}
              <div style={{
                gridColumn: 'span 2',
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: '12px', padding: '16px',
              }}>
                <div style={{
                  fontSize: '10px', color: 'var(--text-muted)', fontWeight: '700',
                  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px',
                }}>
                  💰 Receita vs GMV
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>GMV Total</div>
                    <div style={{ fontSize: '18px', fontWeight: '800', fontFamily: 'Syne, sans-serif', color: 'var(--text-primary)' }}>
                      {fmtKz(U.gmv)}
                    </div>
                  </div>
                  <div style={{ fontSize: '28px', color: 'var(--text-muted)' }}>→</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Receita KiAluga (10%)</div>
                    <div style={{ fontSize: '18px', fontWeight: '800', fontFamily: 'Syne, sans-serif', color: '#059669' }}>
                      {fmtKz(F.receita_bruta)}
                    </div>
                  </div>
                </div>
                <div style={{
                  marginTop: '12px', height: '10px', background: 'var(--border)',
                  borderRadius: '5px', overflow: 'hidden', position: 'relative',
                }}>
                  <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '100%', background: '#D1FAE5' }} />
                  <div style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: `${F.take_rate_efectivo || 10}%`,
                    background: 'linear-gradient(90deg, #059669, #10B981)',
                    borderRadius: '5px',
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  <span>0%</span>
                  <span style={{ fontWeight: '700', color: '#059669' }}>
                    Take rate: {F.take_rate_efectivo || 10}%
                  </span>
                  <span>100%</span>
                </div>
              </div>
            </Seccao>

            {/* ── 5. RETENÇÃO ───────────────────────────────────────────────── */}
            <Seccao
              titulo="Retenção"
              subtitulo="Qualidade da experiência e fidelização de utilizadores"
              cor="#E11D48"
              icone={<StarIcon size={18} color="#fff" />}
            >
              <KpiCard
                label="Taxa de repetição"
                value={fmtPct(R.taxa_repeticao)}
                sub={`${fmtNum(R.clientes_repetidos)} clientes fidelizados`}
                icon={<TrendUpIcon size={14} color="#E11D48" />}
                cor="#E11D48"
              />
              <KpiCard
                label="Avaliação média"
                value={R.avaliacao_media ? `${R.avaliacao_media} ★` : '—'}
                sub="Nota média das avaliações"
                icon={<StarIcon size={14} color="#D97706" />}
                cor="#D97706"
              />
              <KpiCard
                label="Tempo de resolução"
                value={fmtHrs(R.tempo_resolucao_horas)}
                sub="Média para resolver disputas"
                icon={<ClockIcon size={14} color="#0E7490" />}
                cor="#0E7490"
              />

              {/* Dispute status pill strip */}
              <div style={{ gridColumn: 'span 2', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {[
                  { label: 'Total',      value: R.incidentes_disputas || 0, cor: '#64748B', bg: '#F1F5F9' },
                  { label: 'Abertas',    value: R.disputas_abertas    || 0, cor: '#DC2626', bg: '#FEF2F2' },
                  { label: 'Resolvidas', value: R.disputas_resolvidas || 0, cor: '#16A34A', bg: '#F0FDF4' },
                ].map(d => (
                  <div key={d.label} style={{
                    background: d.bg, borderRadius: '10px', padding: '10px 16px',
                    border: `1px solid ${d.cor}20`, flex: 1, minWidth: '80px',
                  }}>
                    <div style={{
                      fontSize: '10px', color: 'var(--text-muted)', fontWeight: '600',
                      textTransform: 'uppercase', letterSpacing: '0.4px',
                    }}>
                      {d.label}
                    </div>
                    <div style={{
                      fontSize: '22px', fontWeight: '800', fontFamily: 'Syne, sans-serif',
                      color: d.cor, marginTop: '4px',
                    }}>
                      {fmtNum(d.value)}
                    </div>
                  </div>
                ))}
              </div>
            </Seccao>

            {/* ── Quick actions bar ──────────────────────────────────────────── */}
            <div style={{
              background: 'var(--card)', border: '1px solid var(--border)',
              borderRadius: '16px', padding: '16px 20px', marginBottom: '20px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: '12px',
              boxShadow: 'var(--shadow-sm)',
            }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                  Acções rápidas
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>
                  Navegar para secções da plataforma
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate('/admin/transacoes')}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  <ReceiptIcon size={12} /> Transações
                </button>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => navigate('/admin/disputas')}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  <AlertIcon size={12} /> Disputas{R.disputas_abertas > 0 ? ` (${R.disputas_abertas})` : ''}
                </button>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => navigate('/admin/utilizadores')}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  <UsersIcon size={12} /> Utilizadores
                </button>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => navigate('/admin/anuncios')}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  <PackageIcon size={12} /> Anúncios
                </button>
              </div>
            </div>

          </>
        )}
      </div>
    </>
  );
}
